/* ============================================================================
 * wishlist.js — Socius Design
 *
 * Single shared client for everything wishlist-related:
 *   - API calls to the Cloudflare Worker
 *   - Modal controller (list picker + "create new list" + login-required)
 *   - Exposes window.SociusWishlist with:
 *       add(item)          → opens modal, lets user pick a list, PUTs to Worker
 *       addMany(items, label) → used by Mood Board "Save as wishlist"
 *       lists()            → fetch all lists (cached 30s)
 *       has(variantId)     → boolean; any list contains this variant
 *       remove(listId, variantId)
 *
 * The modal markup lives in snippets/wishlist-modal.liquid and is rendered
 * once at the bottom of theme.liquid. This file wires it up.
 *
 * Requires window.WISHLIST_AUTH (from snippets/wishlist-auth.liquid).
 * ========================================================================== */
(function () {
  'use strict';

  var auth = window.WISHLIST_AUTH;
  if (!auth) { console.warn('[wishlist] WISHLIST_AUTH missing'); return; }

  // --------- tiny helpers ---------
  function qs(o) {
    return Object.keys(o).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(o[k]);
    }).join('&');
  }
  function api(path, opts) {
    opts = opts || {};
    var u = auth.apiBase + path +
            (path.indexOf('?') === -1 ? '?' : '&') +
            qs({ cid: auth.cid, ts: auth.ts, sig: auth.sig });
    // 20s safety timeout — DELETE without a body, slow worker, or CORS
    // hiccup used to leave callers hanging forever waiting on .then().
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var timeoutId = setTimeout(function () { if (ctrl) ctrl.abort(); }, 20000);

    return fetch(u, {
      method: opts.method || 'GET',
      headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) {
      // Use r.text() first so an empty body (common for DELETE / 204)
      // doesn't blow up r.json() with a SyntaxError that bubbles weirdly.
      return r.text().then(function (text) {
        var j = {};
        if (text) {
          try { j = JSON.parse(text); } catch (_) { j = { _raw: text }; }
        }
        if (!r.ok) throw Object.assign(new Error(j.error || j.message || 'Request failed'), { status: r.status, body: j });
        return j;
      });
    }).then(function (val) {
      clearTimeout(timeoutId);
      return val;
    }, function (err) {
      clearTimeout(timeoutId);
      if (err && err.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw err;
    });
  }
  function uid() { return 'wl_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8); }
  function el(sel) { return document.querySelector(sel); }
  function els(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }

  // --------- cached list index ---------
  var _listsCache = null;
  var _listsFetchedAt = 0;
  function getLists(force) {
    if (!force && _listsCache && (Date.now() - _listsFetchedAt) < 30000) {
      return Promise.resolve(_listsCache);
    }
    return api('/lists').then(function (r) {
      _listsCache = r.lists || [];
      _listsFetchedAt = Date.now();
      return _listsCache;
    });
  }
  function invalidate() { _listsCache = null; }

  // --------- toast ---------
  function toast(msg, kind) {
    var t = document.createElement('div');
    t.className = 'wl-toast wl-toast-' + (kind || 'ok');
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('wl-toast-in'); });
    setTimeout(function () {
      t.classList.remove('wl-toast-in');
      setTimeout(function () { t.remove(); }, 250);
    }, 2600);
  }

  // --------- modal ---------
  // Default copy set; can be overridden per-call via opts.noun or opts.copy.
  var DEFAULT_COPY = {
    noun:            'wishlist',          // lowercase singular
    Noun:            'Wishlist',          // Title-case singular
    title:           'Save to wishlist',
    subtitle:        'Log in or create an account to save items to a wishlist.',
    loginTitle:      'Save to your wishlist',
    newBtn:          '+ Create new wishlist',
    createTitle:     'New wishlist',
    namePlaceholder: 'e.g. Johnson Residence — Living Room',
    toastOne:        'Added to wishlist',
    toastMany:       function (n) { return 'Added ' + n + ' items to wishlist'; },
    toastCreated:    function (name) { return 'Wishlist "' + name + '" created'; },
    emptyMsg:        'No wishlists yet. Create your first one.'
  };
  function buildCopy(opts) {
    if (!opts) return DEFAULT_COPY;
    if (opts.copy) return Object.assign({}, DEFAULT_COPY, opts.copy);
    if (opts.noun) {
      var n  = opts.noun;
      var N  = n.charAt(0).toUpperCase() + n.slice(1);
      return Object.assign({}, DEFAULT_COPY, {
        noun:         n,
        Noun:         N,
        title:        'Save to ' + n,
        subtitle:     'Log in or create an account to save items to a ' + n + '.',
        loginTitle:   'Save to your ' + n,
        newBtn:       '+ Create new ' + n,
        createTitle:  'New ' + n,
        toastOne:     'Added to ' + n,
        toastMany:    function (num) { return 'Added ' + num + ' items to ' + n; },
        toastCreated: function (name) { return N + ' "' + name + '" created'; },
        emptyMsg:     'No ' + (n.slice(-1) === 's' ? n : n + 's') + ' yet. Create your first one.'
      });
    }
    return DEFAULT_COPY;
  }

  var modal = {
    root: null,
    step: null,      // 'login' | 'pick' | 'create'
    pending: null,   // { items: [...], label: '', copy: {...} }
    copy: DEFAULT_COPY,

    ensure: function () {
      if (this.root) return;
      this.root = el('#wl-modal');
      if (!this.root) {
        console.warn('[wishlist] #wl-modal not in DOM — is the wishlist-modal snippet rendered?');
        return;
      }
      var self = this;
      this.root.addEventListener('click', function (e) {
        if (e.target.matches('[data-wl-close]') || e.target === self.root.querySelector('.wl-backdrop')) {
          self.close();
        }
      });
      el('#wl-login-btn') && el('#wl-login-btn').addEventListener('click', function () {
        window.location.href = auth.loginUrl;
      });
      el('#wl-new-btn') && el('#wl-new-btn').addEventListener('click', function () {
        self.show('create');
        setTimeout(function () { var i = el('#wl-new-name'); if (i) i.focus(); }, 30);
      });
      el('#wl-new-cancel') && el('#wl-new-cancel').addEventListener('click', function () {
        self.show('pick');
      });
      el('#wl-new-form') && el('#wl-new-form').addEventListener('submit', function (e) {
        e.preventDefault();
        var name = (el('#wl-new-name').value || '').trim();
        if (!name) return;
        self.createAndSave(name);
      });
    },

    open: function (pending) {
      this.ensure();
      if (!this.root) return;
      this.pending = pending;
      this.copy = pending.copy || DEFAULT_COPY;
      this.applyCopy();
      this.root.classList.add('wl-open');
      document.body.classList.add('wl-modal-open');
      if (!auth.loggedIn) { this.show('login'); return; }
      this.show('pick');
      this.renderLists();
    },

    applyCopy: function () {
      var c = this.copy;
      var set = function (sel, txt) { var n = document.querySelector(sel); if (n) n.textContent = txt; };
      var setAttr = function (sel, attr, txt) { var n = document.querySelector(sel); if (n) n.setAttribute(attr, txt); };
      set('#wl-login-title',  c.loginTitle);
      set('#wl-login-sub',    c.subtitle);
      set('#wl-pick-title',   c.title);
      set('#wl-new-btn',      c.newBtn);
      set('#wl-create-title', c.createTitle);
      setAttr('#wl-new-name', 'placeholder', c.namePlaceholder);
    },

    close: function () {
      if (!this.root) return;
      this.root.classList.remove('wl-open');
      document.body.classList.remove('wl-modal-open');
      this.pending = null;
    },

    show: function (step) {
      this.step = step;
      ['login', 'pick', 'create'].forEach(function (s) {
        var node = el('#wl-step-' + s);
        if (node) node.style.display = (s === step) ? '' : 'none';
      });
    },

    renderLists: function () {
      var host = el('#wl-lists');
      if (!host) return;
      host.innerHTML = '<div class="wl-loading">Loading…</div>';
      var self = this;
      getLists().then(function (lists) {
        if (!lists.length) {
          host.innerHTML = '<div class="wl-empty">' + escapeHtml(self.copy.emptyMsg) + '</div>';
          return;
        }
        host.innerHTML = '';
        lists.forEach(function (L) {
          var count = (typeof L.itemCount === 'number') ? L.itemCount : (L.items ? L.items.length : 0);
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'wl-list-row';
          row.innerHTML =
            '<span class="wl-list-name">' + escapeHtml(L.name) + '</span>' +
            '<span class="wl-list-count">' + count + ' items</span>';
          row.addEventListener('click', function () { self.saveTo(L.id); });
          host.appendChild(row);
        });
      }).catch(function (err) {
        host.innerHTML = '<div class="wl-error">Could not load wishlists: ' + escapeHtml(err.message) + '</div>';
      });
    },

    saveTo: function (listId) {
      var self = this;
      var pending = this.pending;
      if (!pending) return;

      // If the picker was opened with noun === 'mood board', tag the
      // saved list with `board: <name>` so it can be filtered as a
      // mood board (vs. a plain wishlist) by Load pickers and other
      // UIs. The presence of `board` is the canonical mood-board flag.
      var asBoard = self.copy && (self.copy.noun || '').toLowerCase() === 'mood board';
      console.log('[wishlist] saveTo', { listId: listId, noun: self.copy && self.copy.noun, asBoard: asBoard });

      // Merge new items into existing list (by variantId, summing qty)
      api('/lists/' + listId).then(function (r) {
        var list = r.list || { id: listId, name: '', items: [] };
        var byId = {};
        (list.items || []).forEach(function (it) { byId[it.variantId] = it; });
        pending.items.forEach(function (it) {
          if (byId[it.variantId]) {
            byId[it.variantId].qty = (byId[it.variantId].qty || 1) + (it.qty || 1);
          } else {
            byId[it.variantId] = it;
            list.items = list.items || [];
            list.items.push(it);
          }
        });
        var body = { name: list.name, items: list.items };
        // Preserve existing board, OR set it now if this is a mood-board
        // save. Existing wishlists DON'T get retroactively flipped.
        if (list.board) body.board = list.board;
        else if (asBoard) body.board = list.name;
        return api('/lists/' + listId, { method: 'PUT', body: body }).then(function () {
          return body; // pass the saved body forward for the event
        });
      }).then(function (savedBody) {
        invalidate();
        self.close();
        var n = pending.items.length;
        toast(n === 1 ? self.copy.toastOne : self.copy.toastMany(n), 'ok');
        drawer.flashHeaderCount();
        // Notify external code that a save completed. Snippet listens
        // for this to show post-save dialogs.
        try {
          document.dispatchEvent(new CustomEvent('socius:wishlist:saved', {
            bubbles: true,
            detail: {
              listId: listId,
              name:   savedBody.name,
              isBoard: !!savedBody.board,
              noun:   (self.copy && self.copy.noun) || 'wishlist',
              itemCount: n
            }
          }));
        } catch (_) {}
      }).catch(function (err) {
        toast('Could not save: ' + err.message, 'err');
      });
    },

    createAndSave: function (name) {
      var self = this;
      var listId = uid();
      var asBoard = self.copy && (self.copy.noun || '').toLowerCase() === 'mood board';
      var body = { name: name, items: self.pending.items };
      if (asBoard) body.board = name; // mark new list as a mood board
      console.log('[wishlist] createAndSave', { name: name, noun: self.copy && self.copy.noun, asBoard: asBoard, body: body });
      api('/lists/' + listId, {
        method: 'PUT',
        body: body
      }).then(function () {
        invalidate();
        self.close();
        toast(self.copy.toastCreated(name), 'ok');
        try {
          document.dispatchEvent(new CustomEvent('socius:wishlist:saved', {
            bubbles: true,
            detail: {
              listId: listId,
              name:   name,
              isBoard: asBoard,
              noun:   (self.copy && self.copy.noun) || 'wishlist',
              itemCount: (self.pending.items || []).length
            }
          }));
        } catch (_) {}
        drawer.flashHeaderCount();
      }).catch(function (err) {
        toast('Could not create: ' + err.message, 'err');
      });
    }
  };

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // --------- drawer ---------
  // Right-slide drawer that mirrors the cart drawer.
  // Markup lives in snippets/wishlist-drawer.liquid.
  var drawer = {
    root: null,
    wired: false,

    ensure: function () {
      if (this.root) return;
      this.root = el('#wl-drawer');
      if (!this.root) return;
      if (this.wired) return;
      var self = this;
      // Copy the theme's nav/heading font from a live element on the page so
      // the drawer matches regardless of what the theme calls its font
      // settings. We try a few likely selectors in order.
      try {
        var refSelectors = [
          '.main-menu a', '.site-nav a', '.nav-link', '.navigation a',
          '.header__menu a', '.header-menu a', 'header nav a',
          '.logo', '.header__logo', '.site-header__logo',
          'h1', 'h2'
        ];
        var ref = null;
        for (var i = 0; i < refSelectors.length && !ref; i++) {
          ref = document.querySelector(refSelectors[i]);
        }
        if (ref) {
          var cs = window.getComputedStyle(ref);
          if (cs && cs.fontFamily) {
            this.root.style.setProperty('--wl-theme-font', cs.fontFamily);
          }
        }
      } catch (e) { /* non-fatal */ }
      this.root.addEventListener('click', function (e) {
        var t = e.target;
        // Close button: walk up the DOM because the click may land on the
        // inner <svg> or <path>, not the <button data-wl-drawer-close>.
        // Previously used t.matches which only tests the clicked element
        // itself — that's why clicks on the X icon silently failed.
        var closeEl = t.closest && t.closest('[data-wl-drawer-close]');
        if (closeEl) { self.close(); return; }
        var tab = t.closest && t.closest('[data-wl-tab]');
        if (tab) {
          self.setActiveTab(tab.getAttribute('data-wl-tab'));
          return;
        }
        // Moodboard add to cart
        var cartBtn = t.closest && t.closest('[data-wl-mb-cart]');
        if (cartBtn) {
          e.stopPropagation();
          self.addMoodboardToCart(cartBtn.getAttribute('data-wl-mb-cart'));
          return;
        }
        // Moodboard rename
        var renameBtn = t.closest && t.closest('[data-wl-mb-rename]');
        if (renameBtn) {
          e.stopPropagation();
          self.renameMoodboard(renameBtn.getAttribute('data-wl-mb-rename'));
          return;
        }
        // Moodboard delete
        var deleteBtn = t.closest && t.closest('[data-wl-mb-delete]');
        if (deleteBtn) {
          e.stopPropagation();
          self.deleteMoodboard(deleteBtn.getAttribute('data-wl-mb-delete'));
          return;
        }
        // Wishlist rename
        var wlRenameBtn = t.closest && t.closest('[data-wl-list-rename]');
        if (wlRenameBtn) {
          e.stopPropagation();
          self.renameWishlist(wlRenameBtn.getAttribute('data-wl-list-rename'));
          return;
        }
        // Wishlist delete
        var wlDeleteBtn = t.closest && t.closest('[data-wl-list-delete]');
        if (wlDeleteBtn) {
          e.stopPropagation();
          self.deleteWishlist(wlDeleteBtn.getAttribute('data-wl-list-delete'));
          return;
        }
        // Wishlist → Moodboard
        var wlToMbBtn = t.closest && t.closest('[data-wl-list-to-mb]');
        if (wlToMbBtn) {
          e.stopPropagation();
          self.wishlistToMoodboard(wlToMbBtn.getAttribute('data-wl-list-to-mb'));
          return;
        }
        // Moodboard open (name or arrow click)
        var mb = t.closest && t.closest('[data-wl-mb-open]');
        if (mb) {
          self.openMoodboard(mb.getAttribute('data-wl-mb-open'));
          return;
        }
      });
      // ESC closes
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) self.close();
      });
      // Coordinate with the theme's cart drawer: if it opens, close the wishlist drawer.
      var cartDrawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
      if (cartDrawer) {
        var obs = new MutationObserver(function () {
          var openish = cartDrawer.classList.contains('active') ||
                        cartDrawer.classList.contains('is-open') ||
                        cartDrawer.classList.contains('open') ||
                        cartDrawer.getAttribute('open') !== null;
          if (openish && self.isOpen()) self.close();
        });
        obs.observe(cartDrawer, { attributes: true, attributeFilter: ['class', 'open'] });
      }
      this.wired = true;
    },

    isOpen: function () { return this.root && this.root.classList.contains('wl-drawer-open'); },

    open: function () {
      this.ensure();
      if (!this.root) return;
      // Close cart drawer if it's open (best-effort — theme-agnostic)
      try {
        var cd = document.querySelector('cart-drawer.active, #CartDrawer.active, .cart-drawer.active, cart-drawer.is-open, .cart-drawer.open, cart-drawer[open]');
        if (cd) {
          var closeBtn = cd.querySelector('[id*="CartDrawer-Overlay"], .drawer__close, [data-drawer-close], .cart-drawer__close');
          if (closeBtn) closeBtn.click();
          cd.classList.remove('active', 'is-open', 'open');
          cd.removeAttribute('open');
        }
      } catch (_) {}
      this.root.classList.add('wl-drawer-open');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('wl-drawer-open');
      this.render();
    },

    close: function () {
      if (!this.root) return;
      this.root.classList.remove('wl-drawer-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('wl-drawer-open');
    },

    toggle: function () { this.isOpen() ? this.close() : this.open(); },

    render: function () {
      var host = el('#wl-drawer-body');
      if (!host) return;
      if (!auth.loggedIn) return; // server-rendered empty state handles this
      host.innerHTML = '<div class="wl-drawer-loading">Loading…</div>';
      var self = this;
      getLists(true).then(function (lists) {
        self.updateHeaderCount(lists);
        if (!lists.length) {
          host.innerHTML = '<div class="wl-drawer-empty"><p>No wishlists yet.</p><p style="font-size:13px;">Click the ♡ on any product to create your first one.</p></div>';
          return;
        }
        host.innerHTML = '';
        lists.forEach(function (L) {
          host.appendChild(self.buildListNode(L));
        });
      }).catch(function (err) {
        host.innerHTML = '<div class="wl-drawer-error">Could not load: ' + escapeHtml(err.message) + '</div>';
      });
    },

    buildListNode: function (L) {
      var wrap = document.createElement('div');
      wrap.className = 'wl-dl-list';
      wrap.setAttribute('data-list-id', L.id);
      var thumb = L.thumb || (L.items && L.items[0] && L.items[0].image) || '';
      wrap.innerHTML =
        '<div class="wl-dl-head-row" style="position:relative;">' +
          '<button type="button" class="wl-dl-head" aria-expanded="false">' +
            '<span class="wl-dl-head-left">' +
              (thumb ? '<img class="wl-dl-thumb" src="' + escapeHtml(thumb) + '" alt="" loading="lazy">' : '<span class="wl-dl-thumb"></span>') +
              '<span class="wl-dl-name">' + escapeHtml(L.name || 'Untitled') + '</span>' +
            '</span>' +
            '<span class="wl-dl-caret">▸</span>' +
          '</button>' +
          '<span class="wl-dl-actions">' +
            '<button type="button" class="wl-mb-action" data-wl-list-to-mb="' + escapeHtml(L.id) + '" title="Turn into moodboard">◧</button>' +
            '<button type="button" class="wl-mb-action" data-wl-list-rename="' + escapeHtml(L.id) + '" title="Rename">✎</button>' +
            '<button type="button" class="wl-mb-action" data-wl-list-delete="' + escapeHtml(L.id) + '" title="Delete">✕</button>' +
          '</span>' +
        '</div>' +
        '<div class="wl-dl-items"></div>';

      var self = this;
      var head = wrap.querySelector('.wl-dl-head');
      head.addEventListener('click', function () {
        var expanded = wrap.classList.toggle('wl-dl-expanded');
        head.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (expanded) self.loadItems(L.id, wrap.querySelector('.wl-dl-items'));
      });
      return wrap;
    },

    loadItems: function (listId, host) {
      if (host.getAttribute('data-loaded') === '1') return;
      host.innerHTML = '<div class="wl-dl-loading">Loading…</div>';
      var self = this;
      api('/lists/' + listId).then(function (r) {
        var list = r.list || { items: [] };
        host.setAttribute('data-loaded', '1');
        host.innerHTML = '';
        if (!list.items || !list.items.length) {
          host.innerHTML = '<div class="wl-dl-loading">Empty list.</div>';
          return;
        }
        list.items.forEach(function (it) {
          host.appendChild(self.buildItemNode(listId, list, it));
        });
        // Self-heal any legacy items that were saved before the Worker started
        // persisting url/handle. Look them up via Shopify's search-suggest API,
        // rewrite the anchors in-place, then PUT the list back to KV so the
        // backfill is permanent.
        self.backfillUrls(listId, list, host);
      }).catch(function (err) {
        host.innerHTML = '<div class="wl-drawer-error">' + escapeHtml(err.message) + '</div>';
      });
    },

    backfillUrls: function (listId, list, host) {
      var items = list.items || [];
      var needs = items.filter(function (i) { return !i.url && i.title; });
      if (!needs.length) return;

      var lookups = needs.map(function (item) {
        var u = '/search/suggest.json?q=' + encodeURIComponent(item.title) +
                '&resources[type]=product&resources[limit]=5';
        return fetch(u, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data) return;
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            // Prefer an exact title match; fall back to the first hit.
            var match = products.find(function (p) {
              return p.title && item.title && p.title.toLowerCase() === item.title.toLowerCase();
            }) || products[0];
            if (match && (match.url || match.handle)) {
              item.url = match.url || ('/products/' + match.handle);
              if (match.handle) item.handle = match.handle;
            }
          })
          .catch(function () { /* ignore individual failures */ });
      });

      var self = this;
      Promise.all(lookups).then(function () {
        // Re-render any rows where we successfully resolved a URL.
        items.forEach(function (item) {
          if (!item.url) return;
          var row = host.querySelector('.wl-dl-item[data-variant-id="' + item.variantId + '"]');
          if (!row) return;
          var newRow = self.buildItemNode(listId, list, item);
          row.replaceWith(newRow);
        });
        // Persist the healed list back to KV so next open is instant + correct.
        var healed = items.some(function (i) { return i.url; });
        if (healed) {
          api('/lists/' + listId, {
            method: 'PUT',
            body: { name: list.name, items: items, board: list.board }
          }).catch(function () { /* non-fatal */ });
        }
      });
    },

    buildItemNode: function (listId, list, item) {
      var node = document.createElement('div');
      node.className = 'wl-dl-item';
      node.setAttribute('data-variant-id', item.variantId);
      // Resolve a product URL. Prefer item.url; then /products/<handle>; then a
      // storefront search by title as a last resort so legacy items (saved before
      // the Worker started persisting url/handle) still navigate somewhere useful.
      var url = (item.url && item.url.length) ? item.url
              : (item.handle ? '/products/' + item.handle
              : (item.title ? '/search?q=' + encodeURIComponent(item.title) : ''));
      var imgHtml = item.image
        ? '<img class="wl-dl-item-img" src="' + escapeHtml(item.image) + '" alt="" loading="lazy">'
        : '<span class="wl-dl-item-img"></span>';
      var imgWrapped = url
        ? '<a href="' + escapeHtml(url) + '" data-wl-nav style="display:block;">' + imgHtml + '</a>'
        : imgHtml;
      var titleHtml = url
        ? '<a class="wl-dl-item-title" data-wl-nav href="' + escapeHtml(url) + '" style="display:block;color:#111;text-decoration:none;">' + escapeHtml(item.title || 'Item') + '</a>'
        : '<div class="wl-dl-item-title" style="display:block;color:#111;">' + escapeHtml(item.title || 'Item') + '</div>';
      node.innerHTML =
        imgWrapped +
        '<div>' +
          (item.vendor ? '<div class="wl-dl-item-vendor" style="color:#999;font-size:11px;line-height:1.2;letter-spacing:.06em;text-transform:uppercase;font-weight:400;margin-bottom:2px;">' + escapeHtml(item.vendor) + '</div>' : '') +
          titleHtml +
          (item.price ? '<div class="wl-dl-item-price" style="color:#555;margin-top:4px;">' + escapeHtml(String(item.price)) + '</div>' : '') +
        '</div>' +
        '<div class="wl-dl-item-actions">' +
          '<button type="button" class="wl-dl-item-cart" title="Add to cart">Add to cart</button>' +
          '<button type="button" class="wl-dl-item-remove" title="Remove from wishlist">Remove</button>' +
        '</div>';

      var self = this;
      node.querySelector('.wl-dl-item-cart').addEventListener('click', function () {
        // Minimal behavior: just call /cart/add.js and show a toast. Any
        // cart-refresh events were interfering with the theme's own cart
        // drawer remove flow (hang / stalls), so we stay out of the way.
        // The theme's native cart drawer handles its own refresh on
        // /cart/add.js responses in most themes.
        addToShopifyCart(item).then(function () {
          toast('Added to cart', 'ok');
        }).catch(function (err) {
          toast('Could not add: ' + err.message, 'err');
        });
      });
      node.querySelector('.wl-dl-item-remove').addEventListener('click', function () {
        self.removeItem(listId, list, item.variantId, node);
      });
      return node;
    },

    removeItem: function (listId, list, variantId, node) {
      var remaining = (list.items || []).filter(function (i) { return String(i.variantId) !== String(variantId); });
      api('/lists/' + listId, { method: 'PUT', body: { name: list.name, items: remaining } })
        .then(function () {
          list.items = remaining;
          invalidate();
          if (node && node.parentNode) node.parentNode.removeChild(node);
          // Update the count badge on the list head
          var listWrap = document.querySelector('.wl-dl-list[data-list-id="' + listId + '"]');
          if (listWrap) {
            var meta = listWrap.querySelector('.wl-dl-meta span');
            if (meta) meta.textContent = remaining.length + ' items';
          }
          drawer.updateHeaderCount();
        })
        .catch(function (err) { toast('Could not remove: ' + err.message, 'err'); });
    },

    // Update the header badge's count text only. Does NOT show the badge;
    // the badge stays hidden unless flashHeaderCount() is called explicitly
    // (e.g. right after an add-to-wishlist, as a transient confirmation).
    updateHeaderCount: function (lists) {
      var badge = document.querySelector('[data-wl-count]');
      if (!badge) return Promise.resolve(0);
      var resolve = function (ls) {
        var total = (ls || []).reduce(function (n, L) {
          return n + (typeof L.itemCount === 'number' ? L.itemCount : (L.items ? L.items.length : 0));
        }, 0);
        badge.textContent = total;
        return total;
      };
      if (lists) return Promise.resolve(resolve(lists));
      return getLists().then(resolve).catch(function () { return 0; });
    },

    // Briefly show the header badge with the current total, then auto-hide.
    // Use this after a successful add-to-wishlist for lightweight confirmation.
    flashHeaderCount: function () {
      var badge = document.querySelector('[data-wl-count]');
      if (!badge) return;
      this.updateHeaderCount().then(function (total) {
        if (!total) return;
        clearTimeout(badge._hideTimer);
        badge.style.display = '';
        // Force reflow so the animation replays on repeat adds
        badge.classList.remove('wl-flash-in');
        void badge.offsetWidth;
        badge.classList.add('wl-flash-in');
        badge._hideTimer = setTimeout(function () {
          badge.classList.remove('wl-flash-in');
          badge.style.display = 'none';
        }, 1800);
      });
    },

    /* --------- Tabs: Wishlists | Moodboards --------- */

    // Figure out which localStorage key holds this customer's saved moodboards.
    // The mood-board section uses one key for the trade editor and a different
    // key for the retail editor — we read the user-type data attribute (set by
    // wishlist-drawer.liquid from customer.tags) to choose.
    moodboardStorageKey: function () {
      if (!this.root) return null;
      var type = this.root.getAttribute('data-wl-user-type') || 'retail';
      return type === 'trade' ? 'socius_trade_moodboards_v2' : 'socius_retail_wishlists_v1';
    },

    moodboardUrl: function () {
      if (!this.root) return '/';
      var type = this.root.getAttribute('data-wl-user-type') || 'retail';
      var attr = type === 'trade' ? 'data-wl-moodboard-url-trade' : 'data-wl-moodboard-url-retail';
      return this.root.getAttribute(attr) || '/';
    },

    setActiveTab: function (name) {
      if (!this.root) return;
      name = (name === 'moodboards') ? 'moodboards' : 'wishlists';

      // Tab buttons
      var tabs = this.root.querySelectorAll('[data-wl-tab]');
      tabs.forEach(function (btn) {
        var on = btn.getAttribute('data-wl-tab') === name;
        btn.classList.toggle('wl-drawer-tab-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      // Panels
      var wlPanel = this.root.querySelector('#wl-panel-wishlists');
      var mbPanel = this.root.querySelector('#wl-panel-moodboards');
      if (wlPanel) {
        var onW = (name === 'wishlists');
        wlPanel.classList.toggle('wl-drawer-panel-active', onW);
        if (onW) wlPanel.removeAttribute('hidden'); else wlPanel.setAttribute('hidden', '');
      }
      if (mbPanel) {
        var onM = (name === 'moodboards');
        mbPanel.classList.toggle('wl-drawer-panel-active', onM);
        if (onM) mbPanel.removeAttribute('hidden'); else mbPanel.setAttribute('hidden', '');
      }

      // Footer link swaps per tab
      var foot = this.root.querySelector('[data-wl-foot-link]');
      if (foot) {
        if (name === 'moodboards') {
          foot.textContent = 'Open moodboard editor';
          foot.setAttribute('href', this.moodboardUrl());
        } else {
          foot.textContent = 'View all wishlists';
          foot.setAttribute('href', '/pages/wishlists');
        }
      }

      // Lazy-render moodboards on first open
      if (name === 'moodboards' && !this._moodboardsLoaded) {
        this.loadMoodboards();
      }
    },

    loadMoodboards: function () {
      var host = el('#wl-moodboards-body');
      if (!host) return;
      this._moodboardsLoaded = true;

      var key = this.moodboardStorageKey();
      var all = {};
      try {
        all = JSON.parse(localStorage.getItem(key) || '{}') || {};
      } catch (e) { all = {}; }

      // Sort: most recently saved first
      var entries = Object.keys(all).map(function (name) {
        return [name, all[name] || {}];
      }).sort(function (a, b) {
        return (b[1].savedAt || 0) - (a[1].savedAt || 0);
      });

      if (!entries.length) {
        host.innerHTML =
          '<div class="wl-drawer-empty">' +
            '<p>No saved moodboards yet.</p>' +
            '<p style="font-size:13px;">Create one in the moodboard editor — saved boards will appear here.</p>' +
            '<div class="wl-drawer-actions">' +
              '<a class="wl-btn wl-btn-primary" href="' + escapeHtml(this.moodboardUrl()) + '">Open editor</a>' +
            '</div>' +
          '</div>';
        return;
      }

      host.innerHTML = '';
      var self = this;
      entries.forEach(function (pair) {
        host.appendChild(self.buildMoodboardRow(pair[0], pair[1]));
      });
    },

    buildMoodboardRow: function (name, data) {
      var self = this;
      // Prefer an explicit thumb, else first product image from items[]
      var thumb = '';
      if (data && typeof data.thumb === 'string' && data.thumb) {
        thumb = data.thumb;
      } else if (data && Array.isArray(data.items)) {
        for (var i = 0; i < data.items.length; i++) {
          var it = data.items[i];
          if (it && (!it.type || it.type === 'product') && (it.image || it.img || it.imageUrl)) {
            thumb = it.image || it.img || it.imageUrl;
            break;
          }
        }
      }

      var row = document.createElement('div');
      row.className = 'wl-mb-row';
      row.style.position = 'relative';
      row.innerHTML =
        (thumb
          ? '<img class="wl-mb-thumb" src="' + escapeHtml(thumb) + '" alt="" loading="lazy">'
          : '<span class="wl-mb-thumb-placeholder" aria-hidden="true">◧</span>') +
        '<span class="wl-mb-name" data-wl-mb-open="' + escapeHtml(name) + '">' + escapeHtml(name) + '</span>' +
        '<button type="button" class="wl-mb-action" data-wl-mb-cart="' + escapeHtml(name) + '" title="Add all to cart">🛒</button>' +
        '<button type="button" class="wl-mb-action" data-wl-mb-rename="' + escapeHtml(name) + '" title="Rename">✎</button>' +
        '<button type="button" class="wl-mb-action" data-wl-mb-delete="' + escapeHtml(name) + '" title="Delete">✕</button>' +
        '<span class="wl-mb-arrow" data-wl-mb-open="' + escapeHtml(name) + '" aria-hidden="true">›</span>';
      return row;
    },

    renameMoodboard: function (oldName) {
      var self = this;
      var key = this.moodboardStorageKey();
      if (!key) return;
      var host = el('#wl-moodboards-body');
      if (!host) return;

      // Find the row and replace the name span with an input
      var nameEl = host.querySelector('[data-wl-mb-rename="' + oldName + '"]');
      if (!nameEl) return;
      var row = nameEl.closest('.wl-mb-row');
      if (!row) return;
      var nameSpan = row.querySelector('.wl-mb-name');
      if (!nameSpan) return;

      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'wl-mb-rename-input';
      input.value = oldName;
      nameSpan.replaceWith(input);
      input.focus();
      input.select();

      function commit() {
        var newName = input.value.trim();
        if (!newName || newName === oldName) {
          self.loadMoodboards(); // Revert
          return;
        }
        try {
          var all = JSON.parse(localStorage.getItem(key) || '{}');
          if (all[newName]) {
            alert('A board named "' + newName + '" already exists.');
            input.focus();
            return;
          }
          all[newName] = all[oldName];
          delete all[oldName];
          localStorage.setItem(key, JSON.stringify(all));
        } catch (_) {}
        self._moodboardsLoaded = false;
        self.loadMoodboards();
      }

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); self.loadMoodboards(); }
      });
      input.addEventListener('blur', function () {
        // Small delay so Enter handler fires first if needed
        setTimeout(commit, 100);
      });
    },

    deleteMoodboard: function (name) {
      var self = this;
      var key = this.moodboardStorageKey();
      if (!key) return;
      var host = el('#wl-moodboards-body');
      if (!host) return;

      // Find the row and overlay a confirmation
      var deleteBtn = host.querySelector('[data-wl-mb-delete="' + name + '"]');
      if (!deleteBtn) return;
      var row = deleteBtn.closest('.wl-mb-row');
      if (!row) return;

      // Don't add a second confirm if one is already open
      if (row.querySelector('.wl-mb-confirm')) return;

      var overlay = document.createElement('div');
      overlay.className = 'wl-mb-confirm';
      overlay.innerHTML =
        '<p>Delete "<strong>' + escapeHtml(name) + '</strong>"?</p>' +
        '<div class="wl-mb-confirm-actions">' +
          '<button type="button" class="wl-mb-confirm-btn" data-wl-mb-confirm-cancel>Cancel</button>' +
          '<button type="button" class="wl-mb-confirm-btn wl-mb-confirm-btn--danger" data-wl-mb-confirm-yes>Delete</button>' +
        '</div>';
      row.appendChild(overlay);

      overlay.querySelector('[data-wl-mb-confirm-cancel]').addEventListener('click', function (e) {
        e.stopPropagation();
        overlay.remove();
      });
      overlay.querySelector('[data-wl-mb-confirm-yes]').addEventListener('click', function (e) {
        e.stopPropagation();
        try {
          var all = JSON.parse(localStorage.getItem(key) || '{}');
          delete all[name];
          localStorage.setItem(key, JSON.stringify(all));
        } catch (_) {}
        self._moodboardsLoaded = false;
        self.loadMoodboards();
      });
    },

    addMoodboardToCart: function (name) {
      var key = this.moodboardStorageKey();
      if (!key) return;
      try {
        var all = JSON.parse(localStorage.getItem(key) || '{}');
        var data = all[name];
        if (!data || !Array.isArray(data.items)) { toast('Board not found', 'err'); return; }
        var products = data.items.filter(function (i) { return !i.type || i.type === 'product'; });
        if (!products.length) { toast('No products on this board', 'err'); return; }
        // Shopify /cart/add.js accepts { items: [...] } for batch add
        var cartItems = [];
        products.forEach(function (it) {
          var vid = it.variantId || it.productId || it.id;
          if (vid) cartItems.push({ id: Number(vid), quantity: it.qty || 1 });
        });
        if (!cartItems.length) { toast('No valid items to add', 'err'); return; }
        toast('Adding ' + cartItems.length + ' items to cart…');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        }).then(function (r) {
          return r.json().then(function (j) {
            if (r.ok) {
              toast('Added ' + cartItems.length + ' item' + (cartItems.length === 1 ? '' : 's') + ' to cart', 'ok');
            } else {
              toast('Some items could not be added: ' + (j.description || j.message || 'Unknown error'), 'err');
            }
          });
        }).catch(function (err) {
          toast('Could not add to cart: ' + err.message, 'err');
        });
      } catch (err) {
        toast('Could not add to cart', 'err');
      }
    },

    /* ---- Wishlist rename (Cloudflare API) ---- */
    renameWishlist: function (listId) {
      var self = this;
      var wrap = document.querySelector('.wl-dl-list[data-list-id="' + listId + '"]');
      if (!wrap) return;
      var nameSpan = wrap.querySelector('.wl-dl-name');
      if (!nameSpan) return;
      var oldName = nameSpan.textContent;

      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'wl-mb-rename-input';
      input.value = oldName;
      nameSpan.replaceWith(input);
      input.focus();
      input.select();

      var committed = false;
      function commit() {
        if (committed) return;
        committed = true;
        var newName = input.value.trim();
        if (!newName || newName === oldName) {
          self.render(); // revert
          return;
        }
        api('/lists/' + listId).then(function (r) {
          var list = r.list;
          if (!list) throw new Error('List not found');
          return api('/lists/' + listId, {
            method: 'PUT',
            body: { name: newName, items: list.items }
          });
        }).then(function () {
          invalidate();
          toast('Renamed to "' + newName + '"', 'ok');
          self.render();
        }).catch(function (err) {
          toast('Could not rename: ' + err.message, 'err');
          self.render();
        });
      }

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); self.render(); }
      });
      input.addEventListener('blur', function () { setTimeout(commit, 100); });
    },

    /* ---- Wishlist delete with confirmation (Cloudflare API) ---- */
    deleteWishlist: function (listId) {
      var self = this;
      var wrap = document.querySelector('.wl-dl-list[data-list-id="' + listId + '"]');
      if (!wrap) return;
      var headRow = wrap.querySelector('.wl-dl-head-row');
      if (!headRow) return;
      if (headRow.querySelector('.wl-mb-confirm')) return;

      var listName = (wrap.querySelector('.wl-dl-name') || {}).textContent || 'this wishlist';

      var overlay = document.createElement('div');
      overlay.className = 'wl-mb-confirm';
      overlay.innerHTML =
        '<p>Delete "<strong>' + escapeHtml(listName) + '</strong>"?</p>' +
        '<div class="wl-mb-confirm-actions">' +
          '<button type="button" class="wl-mb-confirm-btn" data-wl-confirm-cancel>Cancel</button>' +
          '<button type="button" class="wl-mb-confirm-btn wl-mb-confirm-btn--danger" data-wl-confirm-yes>Delete</button>' +
        '</div>';
      headRow.appendChild(overlay);

      overlay.querySelector('[data-wl-confirm-cancel]').addEventListener('click', function (e) {
        e.stopPropagation();
        overlay.remove();
      });
      overlay.querySelector('[data-wl-confirm-yes]').addEventListener('click', function (e) {
        e.stopPropagation();
        api('/lists/' + listId, { method: 'DELETE' })
          .then(function () {
            invalidate();
            toast('Wishlist deleted', 'ok');
            self.render();
          })
          .catch(function (err) {
            toast('Could not delete: ' + err.message, 'err');
            overlay.remove();
          });
      });
    },

    /* ---- Turn wishlist into moodboard ---- */
    wishlistToMoodboard: function (listId) {
      var self = this;
      toast('Creating moodboard…');
      api('/lists/' + listId).then(function (r) {
        var list = r.list;
        if (!list || !list.items || !list.items.length) {
          toast('Wishlist is empty', 'err');
          return;
        }
        var name = list.name || 'My Wishlist';

        /* --- helpers --- */
        function loadImageDims(url) {
          return new Promise(function (resolve) {
            if (!url) { resolve({ w: 1, h: 1 }); return; }
            var img = new Image();
            img.onload  = function () { resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 }); };
            img.onerror = function () { resolve({ w: 1, h: 1 }); };
            img.crossOrigin = 'anonymous';
            img.src = url;
          });
        }

        function detectWhiteBg(imgSrc) {
          return new Promise(function (resolve) {
            if (!imgSrc) { resolve(false); return; }
            var img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
              try {
                var c = document.createElement('canvas');
                var sz = 80;
                c.width = sz; c.height = sz;
                var ctx = c.getContext('2d');
                ctx.drawImage(img, 0, 0, sz, sz);
                var edge = [];
                for (var x = 0; x < sz; x++) {
                  edge.push(ctx.getImageData(x, 0, 1, 1).data);
                  edge.push(ctx.getImageData(x, sz - 1, 1, 1).data);
                }
                for (var y = 0; y < sz; y++) {
                  edge.push(ctx.getImageData(0, y, 1, 1).data);
                  edge.push(ctx.getImageData(sz - 1, y, 1, 1).data);
                }
                var wc = 0;
                edge.forEach(function (px) {
                  if (px[3] < 60 || (px[0] > 235 && px[1] > 235 && px[2] > 235 && px[3] > 200)) wc++;
                });
                resolve(wc / edge.length > 0.65);
              } catch (_) { resolve(false); }
            };
            img.onerror = function () { resolve(false); };
            img.src = imgSrc;
          });
        }

        function shuffle(arr) {
          for (var i = arr.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
          }
          return arr;
        }

        /* --- analyse every image (dims + white-bg) in parallel --- */
        Promise.all(list.items.map(function (it) {
          return Promise.all([
            loadImageDims(it.image),
            detectWhiteBg(it.image)
          ]).then(function (r) { return { dims: r[0], whiteBg: r[1] }; });
        })).then(function (results) {

          var CANVAS_W = 1400;

          // Build entries for the shared layout engine
          var entries = list.items.map(function (it, idx) {
            var d = results[idx].dims;
            var a = d.w / d.h;
            if (a < 0.4) a = 0.4;
            if (a > 3.0) a = 3.0;
            return { it: it, aspect: a, whiteBg: results[idx].whiteBg };
          });

          var layout = _justifiedLayout(entries, CANVAS_W);

          // Convert layout positions → moodboard item objects
          // Resolve each wishlist item's productId to the moodboard's product
          // lookup key (= first variant id, i.e. SAMPLE_PRODUCTS[n].id).
          // Falls back through: productId → variantId → raw id.
          function resolveProductId(it) {
            if (typeof findProductById === 'function') {
              var p = findProductById(it.productId) || findProductById(it.variantId);
              if (p) return String(p.id);
            }
            return it.productId || it.variantId;
          }

          var mbItems = layout.positions.map(function (pos, zIdx) {
            var it = pos.entry.it;
            var pid = resolveProductId(it);
            // variantId: only set when it differs from the product's default (= pid)
            var vid = it.variantId ? String(it.variantId) : null;
            if (vid === pid) vid = null;
            return {
              uid: 'mb_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6) + '_' + zIdx,
              type: 'product',
              productId: pid,
              variantId: vid,
              x: pos.x, y: pos.y,
              w: pos.w, h: pos.h,
              rot: 0, z: pos.z,
              qty: it.qty || 1, markup: null
            };
          });

          var boardState = {
            projectName: name, clientName: '', projectNumber: '',
            items: mbItems,
            canvasSize: { w: CANVAS_W, h: layout.canvasH },
            zoom: 1, savedAt: Date.now()
          };
          var key = self.moodboardStorageKey();
          if (!key) { toast('Could not determine storage', 'err'); return; }
          var all = JSON.parse(localStorage.getItem(key) || '{}');
          all[name] = boardState;
          localStorage.setItem(key, JSON.stringify(all));
          toast('Moodboard "' + name + '" created');
          self.openMoodboard(name);
        });
      }).catch(function (err) {
        toast('Could not load wishlist: ' + err.message, 'err');
      });
    },

    openMoodboard: function (name) {
      if (!name) return;
      var base = this.moodboardUrl();
      var sep  = (base.indexOf('?') === -1) ? '?' : '&';
      window.location.href = base + sep + 'board=' + encodeURIComponent(name);
    }
  };

  // Add to Shopify cart via AJAX
  function addToShopifyCart(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: item.variantId, quantity: item.qty || 1 })
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart');
        return j;
      });
    });
  }

  // --------- public API ---------
  window.SociusWishlist = {
    _toast: toast,
    drawer: {
      open:   function () { drawer.open(); },
      close:  function () { drawer.close(); },
      toggle: function () { drawer.toggle(); },
      refresh: function () { drawer.render(); }
    },
    add: function (item, opts) {
      // item: { variantId, productId, title, price, image, url, qty }
      // opts (optional): { noun: 'wishlist' | 'item list', copy: {...} }
      modal.open({ items: [normalize(item)], label: item.title, copy: buildCopy(opts) });
    },
    addMany: function (items, labelOrOpts, maybeOpts) {
      // Backwards-compatible:
      //   addMany(items)
      //   addMany(items, 'My label')
      //   addMany(items, { noun: 'item list' })
      //   addMany(items, 'My label', { noun: 'item list' })
      var label, opts;
      if (typeof labelOrOpts === 'string' || labelOrOpts == null) {
        label = labelOrOpts; opts = maybeOpts;
      } else {
        label = null;       opts = labelOrOpts;
      }
      items = (items || []).map(normalize).filter(function (i) { return i.variantId; });
      if (!items.length) { toast('Nothing to save', 'err'); return; }
      modal.open({
        items: items,
        label: label || (items.length + ' items'),
        copy:  buildCopy(opts)
      });
    },
    lists: getLists,
    has: function (variantId) {
      return getLists().then(function (lists) {
        return lists.some(function (L) {
          return (L.items || []).some(function (i) { return String(i.variantId) === String(variantId); });
        });
      });
    },
    remove: function (listId, variantId) {
      return api('/lists/' + listId).then(function (r) {
        var list = r.list;
        if (!list) return;
        list.items = (list.items || []).filter(function (i) { return String(i.variantId) !== String(variantId); });
        return api('/lists/' + listId, { method: 'PUT', body: { name: list.name, items: list.items } });
      }).then(invalidate);
    },

    /* createFromBoard — direct, no-modal wishlist creation.
       Used by the Mood Board "Save as Wishlist" button. If a list with the
       same name already exists, items are merged into it (by variantId,
       summing qty). Otherwise a new list is created. Returns a Promise. */
    createFromBoard: function (name, items) {
      if (!auth.loggedIn) {
        toast('Log in to save a wishlist', 'err');
        return Promise.reject(new Error('Not logged in'));
      }
      items = (items || []).map(normalize).filter(function (i) { return i.variantId; });
      if (!items.length) { toast('No products to save', 'err'); return Promise.reject(new Error('No items')); }

      return getLists(true).then(function (lists) {
        // Check for an existing list with the same name
        var existing = null;
        for (var i = 0; i < lists.length; i++) {
          if (lists[i].name === name) { existing = lists[i]; break; }
        }

        if (existing) {
          // Merge into existing list
          return api('/lists/' + existing.id).then(function (r) {
            var list = r.list || { id: existing.id, name: name, items: [] };
            var byId = {};
            (list.items || []).forEach(function (it) { byId[it.variantId] = it; });
            items.forEach(function (it) {
              if (byId[it.variantId]) {
                byId[it.variantId].qty = (byId[it.variantId].qty || 1) + (it.qty || 1);
              } else {
                byId[it.variantId] = it;
                list.items = list.items || [];
                list.items.push(it);
              }
            });
            return api('/lists/' + existing.id, {
              method: 'PUT',
              body: { name: list.name, items: list.items }
            });
          }).then(function () {
            invalidate();
            toast('Updated wishlist "' + name + '" (' + items.length + ' items)', 'ok');
            drawer.flashHeaderCount();
          });
        } else {
          // Create new list
          var listId = uid();
          return api('/lists/' + listId, {
            method: 'PUT',
            body: { name: name, items: items }
          }).then(function () {
            invalidate();
            toast('Wishlist "' + name + '" created (' + items.length + ' items)', 'ok');
            drawer.flashHeaderCount();
          });
        }
      }).catch(function (err) {
        toast('Could not save wishlist: ' + err.message, 'err');
        throw err;
      });
    }
  };

  function normalize(x) {
    return {
      variantId: x.variantId || x.variant_id || x.id || null,
      productId: x.productId || x.product_id || null,
      title:     x.title || '',
      vendor:    x.vendor || '',
      price:     x.price || '',
      image:     x.image || '',
      url:       x.url || '',
      qty:       x.qty || x.quantity || 1
    };
  }

  // --------- auto-wire [data-wl-drawer-open] (header heart icon etc.) ---------
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wl-drawer-open]');
    if (!btn) return;
    e.preventDefault();
    if (!auth.loggedIn) {
      // Send to login with return_to so the user comes back here; add a hash
      // so we know to auto-open the drawer when they land.
      var current = window.location.pathname + window.location.search + '#wl-open';
      var sep = auth.loginUrl.indexOf('?') === -1 ? '?' : '&';
      window.location.href = auth.loginUrl + sep + 'return_to=' + encodeURIComponent(current);
      return;
    }
    drawer.open();
  });

  // Initialize the drawer root once the DOM is ready so the font-family var
  // gets set from a live nav element. Safe to call more than once.
  setTimeout(function () { drawer.ensure(); }, 0);

  // For logged-in customers: prefetch the count into the badge text (hidden).
  // We never show it at rest — the badge only flashes briefly on a new add.
  if (auth.loggedIn) {
    // Defer slightly so header DOM is settled
    setTimeout(function () { drawer.updateHeaderCount(); }, 250);
    // Auto-open the drawer if we were bounced through login
    if (window.location.hash === '#wl-open') {
      setTimeout(function () {
        drawer.open();
        // Clean the hash so a refresh doesn't re-open the drawer
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }, 300);
    }
  }

  // --------- auto-wire [data-wl-add] buttons ---------
  // Any element with data-wl-add="<json>" or the individual data-* attrs
  // becomes a one-click "add to wishlist" trigger.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wl-add]');
    if (!btn) return;
    e.preventDefault();
    var raw = btn.getAttribute('data-wl-add');
    var item;
    if (raw && raw.charAt(0) === '{') {
      try { item = JSON.parse(raw); } catch (_) {}
    }
    if (!item) {
      item = {
        variantId: btn.getAttribute('data-variant-id'),
        productId: btn.getAttribute('data-product-id'),
        title:     btn.getAttribute('data-title'),
        vendor:    btn.getAttribute('data-vendor'),
        price:     btn.getAttribute('data-price'),
        image:     btn.getAttribute('data-image'),
        url:       btn.getAttribute('data-url'),
        qty:       parseInt(btn.getAttribute('data-qty') || '1', 10)
      };
    }
    // If the button is inside a product form, prefer the selected variant
    var form = btn.closest('form[action*="/cart/add"]');
    if (form) {
      var vsel = form.querySelector('[name="id"]');
      if (vsel && vsel.value) item.variantId = vsel.value;
      var qtyEl = form.querySelector('[name="quantity"]');
      if (qtyEl && qtyEl.value) item.qty = parseInt(qtyEl.value, 10) || 1;
    }
    window.SociusWishlist.add(item);
  });

  // --------- minimal CSS for toast (modal CSS lives in snippet) ---------
  var css = document.createElement('style');
  css.textContent =
    '.wl-toast{position:fixed;left:50%;bottom:32px;transform:translate(-50%,12px);' +
    'background:#111;color:#fff;padding:10px 16px;border-radius:6px;font:500 14px/1.3 system-ui,sans-serif;' +
    'opacity:0;transition:opacity .2s,transform .2s;z-index:99999;max-width:90vw;text-align:center;}' +
    '.wl-toast-in{opacity:1;transform:translate(-50%,0);}' +
    '.wl-toast-err{background:#8b1a1a}';
  document.head.appendChild(css);

  /* ================================================================
     SociusLayout — shared layout engine used by the moodboard editor
     (regenerate button) and the wishlist-to-moodboard conversion.
     Exposed as window.SociusLayout so the section file can call it
     without duplicating ~200 lines of code.
     ================================================================ */

  function _detectWhiteBg(imgSrc) {
    return new Promise(function (resolve) {
      if (!imgSrc) { resolve(false); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var c = document.createElement('canvas');
          var sz = 80;
          c.width = sz; c.height = sz;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, sz, sz);
          var edge = [];
          for (var x = 0; x < sz; x++) {
            edge.push(ctx.getImageData(x, 0, 1, 1).data);
            edge.push(ctx.getImageData(x, sz - 1, 1, 1).data);
          }
          for (var y = 0; y < sz; y++) {
            edge.push(ctx.getImageData(0, y, 1, 1).data);
            edge.push(ctx.getImageData(sz - 1, y, 1, 1).data);
          }
          var wc = 0;
          edge.forEach(function (px) {
            if (px[3] < 60 || (px[0] > 235 && px[1] > 235 && px[2] > 235 && px[3] > 200)) wc++;
          });
          resolve(wc / edge.length > 0.65);
        } catch (_) { resolve(false); }
      };
      img.onerror = function () { resolve(false); };
      img.src = imgSrc;
    });
  }

  function _loadImageDims(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve({ w: 1, h: 1 }); return; }
      var img = new Image();
      img.onload  = function () { resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 }); };
      img.onerror = function () { resolve({ w: 1, h: 1 }); };
      img.crossOrigin = 'anonymous';
      img.src = url;
    });
  }

  function _shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  function _justifiedLayout(entries, canvasW, canvasH) {
    /* Justified-row layout v7.
       - Every row fills the full canvas width → perfect left/right alignment.
       - Heroes (user-tagged or auto-picked) get 1.8× aspect boost.
       - White-bg items placed on perimeter (first/last positions).
       - MIN_H / MAX_H scale with canvas height so square & portrait canvases
         get taller rows and fewer items per row instead of just whitespace. */

    var PAD = 30, GAP = 16;
    var USABLE = canvasW - PAD * 2;
    var HERO_BOOST = 1.8;

    // Scale row height limits with canvas height.  Landscape (900) uses
    // the baseline; taller canvases get proportionally taller rows.
    var hScale = Math.max(1, (canvasH || 900) / 900);
    var MIN_H = Math.round(280 * hScale);
    var MAX_H = Math.round(440 * hScale);

    var whiteBgItems = entries.filter(function (e) { return e.whiteBg; });
    var solidItems   = entries.filter(function (e) { return !e.whiteBg; });
    _shuffle(whiteBgItems);
    _shuffle(solidItems);

    // Pick heroes: honour user-tagged items (item.heroTag) when available,
    // otherwise pick up to 2 random heroes.
    var userHeroes = entries.filter(function (e) { return e.item && e.item.heroTag; });
    var heroCount;
    if (userHeroes.length > 0) {
      // Use user-tagged heroes (all of them)
      heroCount = userHeroes.length;
      userHeroes.forEach(function (e) {
        e._hero = true;
        e._layoutAspect = e.aspect * HERO_BOOST;
      });
    } else {
      // Auto-pick: 2 heroes (or 1 if fewer than 4 items)
      heroCount = entries.length >= 4 ? 2 : (entries.length >= 2 ? 1 : 0);
      var heroPool = solidItems.length >= heroCount ? solidItems : whiteBgItems;
      for (var hi = 0; hi < heroCount && hi < heroPool.length; hi++) {
        heroPool[hi]._hero = true;
        heroPool[hi]._layoutAspect = heroPool[hi].aspect * HERO_BOOST;
      }
    }
    // Non-heroes keep natural aspect
    entries.forEach(function (e) {
      if (!e._hero) e._layoutAspect = e.aspect;
    });

    // White-bg on perimeter, solid in centre
    // (heroes may be in either group — they keep their _hero tag)
    var half = Math.ceil(whiteBgItems.length / 2);
    var ordered = [].concat(
      whiteBgItems.slice(0, half),
      solidItems,
      whiteBgItems.slice(half)
    );

    // Separate heroes for placement strategy
    var heroesInOrder = [];
    ordered = ordered.filter(function (e) {
      if (e._hero) { heroesInOrder.push(e); return false; }
      return true;
    });

    // Tall-hero decision: ~60% of the time when feasible, one hero spans
    // 2 rows vertically like a magazine column layout.
    var tallHero = null, tallSide = 'left', heroColW = 0;
    if (heroesInOrder.length >= 1 && ordered.length >= 4 && Math.random() > 0.4) {
      tallHero = heroesInOrder.shift();
      tallHero._layoutAspect = tallHero.aspect; // natural aspect, no boost
      tallSide = Math.random() > 0.5 ? 'left' : 'right';
      heroColW = Math.round(USABLE * 0.34);
    }

    // Insert remaining in-row heroes back into ordered
    if (heroesInOrder.length >= 1) {
      var pos1 = Math.min(1, ordered.length);
      ordered.splice(pos1, 0, heroesInOrder[0]);
    }
    if (heroesInOrder.length >= 2) {
      var pos2 = Math.min(Math.round(ordered.length * 0.6), ordered.length);
      ordered.splice(pos2, 0, heroesInOrder[1]);
    }

    // Greedy row building using _layoutAspect (inflated for heroes)
    var rows = [];
    var curRow = [], curAspectSum = 0;
    ordered.forEach(function (entry) {
      var testSum = curAspectSum + entry._layoutAspect;
      var testN = curRow.length + 1;
      var testH = (USABLE - (testN - 1) * GAP) / testSum;
      if (curRow.length >= 2 && testH < MIN_H) {
        rows.push(curRow);
        curRow = [entry]; curAspectSum = entry._layoutAspect;
      } else {
        curRow.push(entry); curAspectSum = testSum;
      }
    });
    if (curRow.length) rows.push(curRow);
    // Merge lone last item back
    if (rows.length > 1 && rows[rows.length - 1].length === 1) {
      rows[rows.length - 1 - 1].push(rows.pop()[0]);
    }

    // If tall hero selected but only 1 row, fall back to normal wide hero
    if (tallHero && rows.length < 2) {
      tallHero._layoutAspect = tallHero.aspect * HERO_BOOST;
      // Insert back as a wide hero at position 1
      var fb = Math.min(1, ordered.length);
      ordered.splice(fb, 0, tallHero);
      tallHero = null; heroColW = 0;
      // Rebuild rows with this item included
      rows = []; curRow = []; curAspectSum = 0;
      ordered.forEach(function (entry) {
        var testSum = curAspectSum + entry._layoutAspect;
        var testN = curRow.length + 1;
        var testH = (USABLE - (testN - 1) * GAP) / testSum;
        if (curRow.length >= 2 && testH < MIN_H) {
          rows.push(curRow);
          curRow = [entry]; curAspectSum = entry._layoutAspect;
        } else {
          curRow.push(entry); curAspectSum = testSum;
        }
      });
      if (curRow.length) rows.push(curRow);
      if (rows.length > 1 && rows[rows.length - 1].length === 1) {
        rows[rows.length - 1 - 1].push(rows.pop()[0]);
      }
    }

    // Pick which 2 rows the tall hero spans (first two by default)
    var spanStart = 0;

    // Position items — rows in the span zone get reduced width
    var cursorY = PAD, positions = [], zIdx = 0;
    var rowYs = [], rowHs = [];
    rows.forEach(function (row, rowIdx) {
      var isSpan = tallHero && (rowIdx === spanStart || rowIdx === spanStart + 1);
      var rowUsable = isSpan ? (USABLE - heroColW - GAP) : USABLE;

      var aspectSum = 0;
      row.forEach(function (e) { aspectSum += e._layoutAspect; });
      var gapTotal = (row.length - 1) * GAP;
      var rowH = Math.round((rowUsable - gapTotal) / aspectSum);
      rowH = Math.max(MIN_H, Math.min(MAX_H, rowH));

      var targetW = rowUsable - gapTotal;
      var widths = row.map(function (e) {
        return Math.round(targetW * (e._layoutAspect / aspectSum));
      });
      var totalW = 0;
      widths.forEach(function (w) { totalW += w; });
      var diff = targetW - totalW;
      var wIdx = 0;
      widths.forEach(function (w, i) { if (w > widths[wIdx]) wIdx = i; });
      widths[wIdx] += diff;

      var baseX = PAD;
      if (isSpan && tallSide === 'left') baseX = PAD + heroColW + GAP;
      var cursorX = baseX;
      row.forEach(function (entry, i) {
        positions.push({ entry: entry, item: entry.item, x: cursorX, y: cursorY, w: widths[i], h: rowH, z: zIdx + 1 });
        cursorX += widths[i] + GAP;
        zIdx++;
      });
      rowYs.push(cursorY);
      rowHs.push(rowH);
      cursorY += rowH + GAP;
    });

    // Place the tall hero spanning 2 rows
    if (tallHero && rows.length >= 2) {
      var thY = rowYs[spanStart];
      var thH = (rowYs[spanStart + 1] + rowHs[spanStart + 1]) - thY;
      var thX = tallSide === 'left' ? PAD : (PAD + USABLE - heroColW);
      positions.push({
        entry: tallHero, item: tallHero.item,
        x: thX, y: thY, w: heroColW, h: thH, z: zIdx + 1
      });
      zIdx++;
    }

    // Scale content to fill canvas when it's shorter than the target height.
    var contentH = cursorY - GAP - PAD;
    var targetH = canvasH || 900;
    var usableH = targetH - PAD * 2;
    if (contentH > 0 && contentH < usableH * 0.92) {
      var scale = usableH / contentH;
      positions.forEach(function (p) {
        p.y = PAD + Math.round((p.y - PAD) * scale);
        p.h = Math.round(p.h * scale);
      });
      contentH = Math.round(contentH * scale);
    }

    var finalH = Math.max(targetH, contentH + PAD * 2);

    // Vertically centre the block if the canvas is taller than the content
    var yOff = Math.round((finalH - contentH) / 2) - PAD;
    if (yOff > 0) positions.forEach(function (p) { p.y += yOff; });
    canvasH = finalH;

    // Clean up temp properties
    entries.forEach(function (e) { delete e._hero; delete e._layoutAspect; });

    return { positions: positions, canvasH: canvasH };
  }

  window.SociusLayout = {
    detectWhiteBg: _detectWhiteBg,
    loadImageDims: _loadImageDims,
    shuffle: _shuffle,
    justifiedLayout: _justifiedLayout,

    /** Optimize Layout — scales and centres the existing layout on the canvas.
     *  Does NOT rearrange items.  Preserves relative placement, just fits
     *  everything proportionally into the current canvas size and centres it.
     *  @param {object[]} items   – product items from state.items
     *  @param {number}   cw      – canvas width
     *  @param {number}   ch      – canvas height
     *  @returns {{canvasH: number}} — items are mutated in-place */
    optimize: function (items, cw, ch) {
      if (!items.length) return { canvasH: ch || 900 };
      var PAD = 30;

      // 1. Compute bounding box of current items
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      items.forEach(function (it) {
        if (it.x < minX) minX = it.x;
        if (it.y < minY) minY = it.y;
        if (it.x + it.w > maxX) maxX = it.x + it.w;
        if (it.y + it.h > maxY) maxY = it.y + it.h;
      });
      var bw = maxX - minX;
      var bh = maxY - minY;
      if (bw <= 0 || bh <= 0) return { canvasH: ch || 900 };

      // 2. Scale to fit within canvas (with padding), preserving aspect ratio
      var availW = cw - PAD * 2;
      var availH = (ch || 900) - PAD * 2;
      var scale = Math.min(availW / bw, availH / bh, 1.3);  // cap at 1.3× to avoid over-zoom

      // 3. Translate so bounding box starts at origin, scale, then centre
      var newBW = bw * scale;
      var newBH = bh * scale;
      var offX = PAD + Math.round((availW - newBW) / 2);
      var offY = PAD + Math.round((availH - newBH) / 2);

      items.forEach(function (it) {
        it.x = offX + Math.round((it.x - minX) * scale);
        it.y = offY + Math.round((it.y - minY) * scale);
        it.w = Math.round(it.w * scale);
        it.h = Math.round(it.h * scale);
      });

      var finalH = Math.max(ch || 900, newBH + PAD * 2);
      return { canvasH: finalH };
    },

    /** Called by the moodboard editor's Regenerate Layout button.
     *  @param {string[]} imgUrls  – image URL for each product item
     *  @param {object[]} items    – the state.items product array
     *  @param {number}   canvasW  – current canvas width
     *  @param {number}   [canvasH] – current canvas height (layout grows to fit if needed)
     *  @returns {Promise<{positions, canvasH}>} */
    regenerate: function (imgUrls, items, canvasW, canvasH) {
      return Promise.all(imgUrls.map(function (url) {
        return Promise.all([_detectWhiteBg(url), _loadImageDims(url)])
          .then(function (r) { return { whiteBg: r[0], dims: r[1] }; });
      })).then(function (results) {
        var entries = items.map(function (item, idx) {
          var d = results[idx].dims;
          var a = d.w / d.h;
          if (a < 0.4) a = 0.4;
          if (a > 3.0) a = 3.0;
          return { item: item, aspect: a, whiteBg: results[idx].whiteBg };
        });
        return _justifiedLayout(entries, canvasW, canvasH);
      });
    }
  };

})();